import { isBefore, isValid, parse } from "date-fns";
import { z } from "zod";

export const initialSchema = z.object({
  name: z
    .string({ required_error: "Obrigatório" })
    .min(1, "Nome é obrigatório"),
  phone: z
    .string({ required_error: "Obrigatório" })
    .min(1, "Telefone é obrigatório"),
  cpf: z
    .string({ required_error: "Obrigatório" })
    .min(1, "CPF é obrigatório")
    .refine((value) => {
      const cpf = value.replace(/\D/g, "");
      return cpf.length === 11;
    }, "CPF deve conter 11 dígitos")
    .transform((value) => value.replace(/\D/g, "")),
  birthDate: z
    .string({ required_error: "Obrigatório" })
    .min(10, "A Data é obrigatória")
    .refine((value) => {
      return isValid(parse(value, "dd/MM/yyyy", new Date()));
    }, "A Data é inválida"),
  zipCode: z
    .string()
    .min(8, "CEP deve ter 8 dígitos")
    .max(9, "CEP inválido")
    .transform((value) => value?.replace(/\D/g, ""))
    .optional(),
});

const bankAccountSchema = z.object({
  bank: z.string().min(1, "Banco é obrigatório"),
  agency: z
    .string()
    .min(3, "Agência é obrigatória")
    .max(4, "No maximo 4 caracteres"),
  account: z.string().min(4, "Conta é obrigatória"),
  accountType: z.string().min(1, "Tipo de conta é obrigatório"),
});

const addressSchema = z.object({
  zipCode: z.string().min(8, "CEP deve ter 8 dígitos").max(9, "CEP inválido"),
  street: z.string().min(1, "Rua é obrigatória"),
  number: z
    .string()
    .min(1, "Número é obrigatório")
    .transform((value) => value?.replace(/\D/g, "")),
  complement: z.string().optional(),
  neighborhood: z.string().min(1, "Bairro é obrigatório"),
  city: z.string().min(1, "Cidade é obrigatória"),
  state: z.string().min(2, "Estado é obrigatório"),
});

export const crefazSchema = z.object({
  email: z.string().email("Email inválido"),
  crefaz: z.object({
    adicionais: z.any(),
    value: z.coerce.number().min(1, "Valor é obrigatório").default(0),
    installment: z
      .object({
        prazo: z.number().min(1, "Prazo é obrigatório"),
        valor: z.number().min(1, "Valor é obrigatório"),
      })
      .refine((data) => data.prazo > 0 && data.valor > 0, {
        message: "Prazo e valor são obrigatórios",
        path: ["installment"],
      }),
  }),
});

export const crefazStepSchema = initialSchema.merge(crefazSchema);

export const finalSchema = z.object({
  email: z.string().email("Email inválido"),
  rg: z.object({
    number: z.string().min(1, "Número do RG é obrigatório"),
    date: z
      .string()
      .min(10, "Data de emissão é obrigatória")
      .refine((value) => {
        return isValid(parse(value, "dd/MM/yyyy", new Date()));
      }, "A Data é inválida")
      .refine((value) => {
        return isBefore(parse(value, "dd/MM/yyyy", new Date()), new Date());
      }, "Data invalida"),
    uf: z.string().min(1, "UF de emissão é obrigatória"),
    org: z.string().min(1, "Órgão emissor é obrigatório"),
  }),

  address: addressSchema,
  bankAccount: bankAccountSchema,
});

export const crefazFinalSchema = finalSchema.extend({
  placeOfBirth: z.object({
    uf: z.string().min(1, "UF de naturalidade é obrigatória"),
    city: z.string().min(1, "Cidade de naturalidade é obrigatória"),
  }),
});

export type AllSchemas = z.infer<typeof initialSchema> &
  z.infer<typeof finalSchema> &
  Partial<z.infer<typeof crefazFinalSchema>>;
